import React from "react";
import { motion } from "framer-motion";

/* ═══════════════════════════════════════════════════════════════
   VawWorldOS — the proprietary production tracking + comms
   platform. This section shows a live mock of the producer console
   so buyers can see it, not just read about it.
   ═══════════════════════════════════════════════════════════════ */

const MODULES = [
  {
    id: "dashboard",
    label: "Live Dashboard",
    kicker: "01",
    title: "Every asset. Every state. In one glance.",
    body: "6,412 shots. 148 assets. Twelve departments. Live status pinned to the same clock your team is on — never a spreadsheet emailed on Friday.",
  },
  {
    id: "dailies",
    label: "Cloud Dailies",
    kicker: "02",
    title: "Watch what your team just shipped.",
    body: "Versioned takes, threaded director notes, timecoded frames. Approve, redline, or ask — from any browser, any timezone.",
  },
  {
    id: "budget",
    label: "Live Budget",
    kicker: "03",
    title: "Where the money is, in real time.",
    body: "Actuals vs. bid, department by department. Overage triggers, currency locks, invoice pre-approvals. Your CFO stops asking for updates.",
  },
  {
    id: "chat",
    label: "Producer Comms",
    kicker: "04",
    title: "One channel. No email chains.",
    body: "Threaded per-shot conversation, supervisor pings, and encrypted file drops — TPN-aligned. What was decided, when, by whom.",
  },
];

const ASSET_ROWS = [
  { id: "SH_0142", name: "Rooftop Chase — Wide", dept: "LGT",  status: "Rendering",  pct: 78, sev: "info" },
  { id: "SH_0143", name: "Rooftop Chase — Push", dept: "ANIM", status: "In review",  pct: 100, sev: "warn" },
  { id: "SH_0144", name: "Bridge — Panic",       dept: "FX",   status: "In progress", pct: 42, sev: "info" },
  { id: "SH_0145", name: "Bridge — Aftermath",   dept: "CMP",  status: "Client note", pct: 90, sev: "alert" },
  { id: "AS_0071", name: "Hero rig — v14",       dept: "RIG",  status: "Approved",    pct: 100, sev: "ok" },
  { id: "AS_0072", name: "City block — LOD2",    dept: "MDL",  status: "QC",          pct: 88, sev: "info" },
];

const SEV_COLOR = {
  ok:    { dot: "var(--vw-cyan)",     text: "var(--vw-cyan)",     bar: "var(--vw-cyan)" },
  info:  { dot: "#8AB4FF",           text: "var(--vw-text)",     bar: "#8AB4FF"        },
  warn:  { dot: "#FFB86B",           text: "var(--vw-orange)",   bar: "#FFB86B"        },
  alert: { dot: "#FF6B8A",           text: "#FF6B8A",           bar: "#FF6B8A"         },
};

export default function VawWorldOS() {
  return (
    <section id="vawworldos" className="relative border-t border-[var(--vw-border)] bg-[var(--vw-bg)]" data-testid="vw-vawworldos">
      {/* Ambient background grid */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" aria-hidden>
        <svg width="100%" height="100%">
          <defs>
            <pattern id="oxGrid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M60 0H0V60" fill="none" stroke="currentColor" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#oxGrid)" className="text-[var(--vw-cyan)]"/>
        </svg>
      </div>

      <div className="vw-container relative py-20 md:py-28">
        {/* ─── Header ─────────────────────────────────────────── */}
        <div className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-3xl">
            <div className="vw-eyebrow mb-6 flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[var(--vw-cyan)]/40 text-[var(--vw-cyan)]">
                <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
                LIVE
              </span>
              VAWWORLDOS · PROPRIETARY PRODUCTION CONSOLE
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
              The producer console we built <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">so you don't have to email us</span>.
            </h2>
          </div>
          <p className="max-w-md text-[var(--vw-text-dim)] leading-relaxed">
            Every shot. Every asset. Every dollar. Every note. In one browser tab. Your buyer can log in and see the same frame we do.
          </p>
        </div>

        {/* ─── The mock dashboard ─────────────────────────────── */}
        <div className="relative border border-[var(--vw-border-strong)] bg-[var(--vw-surface)] overflow-hidden shadow-[0_40px_120px_-30px_rgba(0,229,255,0.15)]" data-testid="vw-os-dashboard">
          {/* Chrome bar */}
          <div className="flex items-center justify-between gap-4 border-b border-[var(--vw-border)] bg-black/40 px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[color:rgba(255,107,138,0.6)]" />
              <span className="w-2 h-2 rounded-full bg-[color:rgba(255,184,107,0.6)]" />
              <span className="w-2 h-2 rounded-full bg-[color:rgba(0,229,255,0.6)]" />
              <span className="ml-4 text-[var(--vw-text)]">vawworldos.app / features / RIVERKING</span>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <span>PROD · <span className="text-[var(--vw-cyan)]">S03 · WEEK 42</span></span>
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />LIVE · MUMBAI · 14:07</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_320px]">
            {/* ─ Sidebar ─────────────────────────────────────── */}
            <aside className="border-r border-[var(--vw-border)] p-4 hidden lg:block">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-3">Modules</div>
              <ul className="space-y-1">
                {[
                  ["Dashboard", true],
                  ["Shots · 6412", false],
                  ["Assets · 148", false],
                  ["Dailies", false],
                  ["Budget", false],
                  ["Notes · 42", false],
                  ["Deliveries", false],
                  ["Vendors", false],
                ].map(([lbl, active]) => (
                  <li key={lbl} className={`flex items-center gap-2 px-2 py-1.5 text-[12px] font-mono ${active ? "bg-[var(--vw-cyan)]/10 text-[var(--vw-cyan)] border-l-2 border-[var(--vw-cyan)]" : "text-[var(--vw-text-dim)]"}`}>
                    <span className="w-1 h-1 rounded-full bg-current opacity-70" />
                    {lbl}
                  </li>
                ))}
              </ul>

              <div className="mt-8 border-t border-[var(--vw-border)] pt-5">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-3">Departments</div>
                <div className="space-y-2.5 font-mono text-[11px]">
                  {[
                    ["ANIM", 82],
                    ["LGT",  64],
                    ["FX",   38],
                    ["CMP",  21],
                    ["MDL",  95],
                  ].map(([d, p]) => (
                    <div key={d}>
                      <div className="flex justify-between text-[var(--vw-text-dim)]"><span>{d}</span><span className="text-[var(--vw-text)]">{p}%</span></div>
                      <div className="h-[3px] bg-[var(--vw-border)] mt-1 overflow-hidden">
                        <div className="h-full bg-[var(--vw-cyan)]/70" style={{ width: `${p}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </aside>

            {/* ─ Main : shot table + charts ───────────────────── */}
            <div className="min-h-[520px] p-5 md:p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  ["Shots on cadence", "94.2%", "+2.1"],
                  ["Weekly burn",      "$118k", "on-bid"],
                  ["Open notes",        "12",   "−4"],
                  ["Days to lock",      "31",   "−1"],
                ].map(([k, v, delta], idx) => (
                  <div key={k} className="border border-[var(--vw-border)] bg-black/40 p-3">
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.24em] text-[var(--vw-text-mute)] mb-1">{k}</div>
                    <div className="font-display text-2xl md:text-3xl leading-none tracking-tight text-[var(--vw-text)]">{v}</div>
                    <div className={`mt-1 font-mono text-[10px] ${idx === 0 || idx === 2 || idx === 3 ? "text-[var(--vw-cyan)]" : "text-[var(--vw-text-mute)]"}`}>{delta}</div>
                  </div>
                ))}
              </div>

              {/* Shot table */}
              <div className="border border-[var(--vw-border)]">
                <div className="grid grid-cols-[80px_1fr_60px_1fr_80px] bg-black/40 font-mono text-[9.5px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
                  <div className="p-2.5">ID</div>
                  <div className="p-2.5 border-l border-[var(--vw-border)]">Name</div>
                  <div className="p-2.5 border-l border-[var(--vw-border)]">Dept</div>
                  <div className="p-2.5 border-l border-[var(--vw-border)]">Status</div>
                  <div className="p-2.5 border-l border-[var(--vw-border)] text-right">%</div>
                </div>
                {ASSET_ROWS.map((r, i) => {
                  const col = SEV_COLOR[r.sev];
                  const pct = r.pct;
                  return (
                    <div key={r.id} className="grid grid-cols-[80px_1fr_60px_1fr_80px] border-t border-[var(--vw-border)] font-mono text-[11px] hover:bg-black/30 transition-colors">
                      <div className="p-2.5 text-[var(--vw-text-mute)]">{r.id}</div>
                      <div className="p-2.5 border-l border-[var(--vw-border)] text-[var(--vw-text)]">{r.name}</div>
                      <div className="p-2.5 border-l border-[var(--vw-border)] text-[var(--vw-text-dim)]">{r.dept}</div>
                      <div className="p-2.5 border-l border-[var(--vw-border)] flex items-center gap-2">
                        <span className="w-1.5 h-1.5" style={{ background: col.dot }} />
                        <span style={{ color: col.text }}>{r.status}</span>
                      </div>
                      <div className="p-2.5 border-l border-[var(--vw-border)]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-[3px] bg-[var(--vw-border)] overflow-hidden">
                            <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: col.bar }} />
                          </div>
                          <span className="text-[var(--vw-text)] w-8 text-right">{pct}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Timeline bar */}
              <div className="mt-6 border border-[var(--vw-border)] p-4">
                <div className="flex items-center justify-between mb-3 font-mono text-[9.5px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
                  <span>Master schedule · S03</span>
                  <span className="text-[var(--vw-cyan)]">On cadence · +2 days buffer</span>
                </div>
                <div className="relative h-3 bg-[var(--vw-border)]">
                  <div className="absolute inset-y-0 left-0 bg-[var(--vw-cyan)]/25" style={{ width: "62%" }} />
                  <div className="absolute inset-y-0 w-[2px] bg-[var(--vw-cyan)] vw-os-scrub" />
                </div>
                <div className="mt-2 grid grid-cols-6 font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
                  <span>Story</span><span>Design</span><span>Blocking</span><span className="text-[var(--vw-cyan)]">Anim</span><span>Lgt</span><span>DCP</span>
                </div>
              </div>
            </div>

            {/* ─ Right rail : activity + notes ───────────────── */}
            <aside className="border-l border-[var(--vw-border)] p-5 hidden lg:block">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-3">Live activity</div>
              <ul className="space-y-3 text-[12px] font-mono">
                {[
                  ["14:07", "SH_0143 · approved by A. Rao", "ok"],
                  ["14:04", "SH_0145 · client note added",  "alert"],
                  ["13:58", "SH_0144 · FX cache queued",    "info"],
                  ["13:51", "AS_0071 · rig v14 promoted",    "ok"],
                  ["13:44", "SH_0142 · render 78% frame",   "info"],
                  ["13:30", "Weekly PDF exported to buyer",  "info"],
                ].map(([time, msg, sev]) => (
                  <li key={msg} className="flex gap-3 items-start">
                    <span className="text-[var(--vw-text-mute)] w-9 shrink-0">{time}</span>
                    <span className="w-1.5 h-1.5 mt-1.5 shrink-0" style={{ background: SEV_COLOR[sev].dot }} />
                    <span className="text-[var(--vw-text-dim)] leading-snug">{msg}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 border-t border-[var(--vw-border)] pt-5">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-3">Open note · Producer</div>
                <div className="border border-[var(--vw-border)] bg-black/40 p-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-cyan)] mb-2">SH_0145 · 00:04:22:14</div>
                  <p className="font-serif-italic text-[13px] text-[var(--vw-text)] leading-snug">
                    "Camera drift on the aftermath push feels heavy — pull 20% and add rim on the hero."
                  </p>
                  <div className="mt-3 flex items-center justify-between font-mono text-[9.5px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
                    <span>— Producer · Streamer</span>
                    <span className="text-[var(--vw-cyan)]">2 replies</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* ─── Modules explainer ──────────────────────────────── */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--vw-border)]">
          {MODULES.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="bg-[var(--vw-bg)] p-7 md:p-8 hover:bg-[var(--vw-surface)] transition-colors group"
              data-testid={`vw-os-module-${m.id}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-4 flex items-center gap-3">
                <span className="text-[var(--vw-cyan)]">{m.kicker}</span>
                <span className="w-6 h-px bg-[var(--vw-border-strong)]" />
                {m.label}
              </div>
              <h3 className="font-display text-2xl md:text-[26px] leading-[1.1] mb-4 text-[var(--vw-text)] tracking-tight">
                {m.title}
              </h3>
              <p className="text-[14px] text-[var(--vw-text-dim)] leading-relaxed">
                {m.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* ─── CTA row ────────────────────────────────────────── */}
        <div className="mt-14 flex flex-col md:flex-row md:items-center gap-6 md:justify-between border border-[var(--vw-cyan)]/40 bg-[color:rgba(0,229,255,0.04)] p-6 md:p-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] mb-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
              LIVE NOW · vawworld.in · No credit card · No sales call
            </div>
            <div className="font-display text-2xl md:text-3xl lg:text-[34px] leading-[1.08] tracking-tight text-[var(--vw-text)] max-w-[26ch]">
              Login and experience <span className="vw-text-gradient">the magic — for free</span>
              <span className="text-[var(--vw-cyan)]">.</span>
            </div>
            <p className="mt-3 text-[13px] text-[var(--vw-text-dim)] max-w-md leading-relaxed">
              Open a real, redacted show in your browser. Track shots, watch dailies, and build your first budget — before you talk to anyone.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
            <a
              href="https://www.vawworld.in"
              target="_blank"
              rel="noopener noreferrer"
              className="vw-btn-primary vw-btn-glow vw-focus"
              data-testid="vw-os-cta"
            >
              Login &amp; Start Budgeting — Free
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </a>
            <a href="#pipeline" className="vw-btn-ghost vw-focus" data-testid="vw-os-cta-secondary">
              See how it plugs into the pipeline
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
